import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchRelocationForm } from "../../../store/slices/relocationFormsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function RelocationFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const relocationState = useSelector((state) => state.relocationForms || {});
    const { currentRelocationForm, isLoading, error } = relocationState;

    useEffect(() => {
        if (id) dispatch(fetchRelocationForm(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentRelocationForm) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentRelocationForm, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Relocation Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentRelocationForm) return <div className="p-8 text-center">No record found.</div>;

    const d = currentRelocationForm;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    // Medicine list helper to avoid repetition
    const meds = [
        { key: "quetiapine", label: "Tab Quetiapine" },
        { key: "serenace", label: "Inj Serenace" },
        { key: "avil", label: "Inj Avil" },
        { key: "clopixol", label: "Inj Clopixol" },
        { key: "dormicum", label: "Inj Dormicum" },
        { key: "vallium", label: "Inj Vallium" },
        { key: "procyclidine", label: "Tab Procyclidine" },
    ];

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 11px !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .header h6 { font-size: 1rem !important; text-decoration: underline !important; }
                
                .section-title { background: #eee !important; padding: 4px 8px !important; font-weight: bold !important; margin: 15px 0 8px 0 !important; border: 1px solid #000 !important; display: block !important; text-transform: uppercase !important; }
                
                .info-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 10px !important; }
                .info-table td { border: 1px solid #000 !important; padding: 6px !important; vertical-align: top !important; }
                .label { font-weight: bold !important; width: 25% !important; background: #f9f9f9 !important; }

                .sign-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 10px !important; }
                .sign-table td { border: 1px solid #000 !important; padding: 3px !important; vertical-align: top !important; }
                .sign-label1, .value1 { font-weight: bold !important; width: 10% !important; background: #f9f9f9 !important; }
                .sign-label2, .value2 { font-weight: bold !important; width: 20% !important; background: #f9f9f9 !important; }
                
                .med-table { width: 100% !important; border-collapse: collapse !important; margin-top: 5px !important; }
                .med-table th, .med-table td { border: 1px solid #000 !important; padding: 5px !important; text-align: center !important; }
                .med-table th { background: #f2f2f2 !important; font-weight: bold !important; }

                .text-box { border: 1px solid #000 !important; padding: 8px !important; min-height: 50px !important; margin-top: 5px !important; }
                
                .footer { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 10px !important; }
                .sig-area { display: flex !important; justify-content: space-between !important; margin-top: 30px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 45% !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>RELOCATION FORM</h6>
                </div>

                {/* Document Section */}
                <span className="section-title">Document Information</span>
                <table className="sign-table">
                    <tbody>
                        <tr>
                            <td className="label1">Document Name</td>
                            <td className="value1">{d.standard_name || d.standardName}</td>
                            <td className="label1">Document Number</td>
                            <td className="value2">{d.document_number || d.documentNumber}</td>
                            <td className="label1">Revision Number</td>
                            <td className="value2">{d.revision_number || d.revisionNumber}</td>
                        </tr>
                        <tr>
                            <td className="label1">Department</td>
                            <td className="value1">{d.department_name || d.departmentName}</td>
                            <td className="label1">Issue Date</td>
                            <td className="value2">{fmtDate(d.issue_date || d.issueDate)}</td>
                            <td className="label1">Review Date</td>
                            <td className="value2">{fmtDate(d.review_date || d.reviewDate)}</td>
                        </tr>
                    </tbody>
                </table>
                {/* Patient Info Section */}
                <span className="section-title">Patient Information</span>
                <table className="info-table">
                    <tbody>
                        <tr>
                            <td className="label">Patient Name</td>
                            <td>{d.patient_name || d.patientName}</td>
                            <td className="label">Gender</td>
                            <td>{d.patient_gender || d.patientGender}</td>
                        </tr>
                        <tr>
                            <td className="label">Date of Birth</td>
                            <td>{fmtDate(d.patient_dob || d.patientDob)}</td>
                            <td className="label">Contact</td>
                            <td>{d.patient_contact || d.patientContact}</td>
                        </tr>
                        <tr>
                            <td className="label">Current Medication</td>
                            <td colSpan="3">{d.current_medication || d.currentMedication}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Medical Administration Table */}
                <span className="section-title">Medication Administration (Doctor vs RN)</span>
                <table className="med-table">
                    <thead>
                        <tr>
                            <th rowSpan="2">Medication</th>
                            <th colSpan="2">Doctor / Consultant</th>
                            <th colSpan="2">RN / Dispenser</th>
                        </tr>
                        <tr>
                            <th>Given?</th>
                            <th>Dose / Route</th>
                            <th>Given?</th>
                            <th>Dose / Route</th>
                        </tr>
                    </thead>
                    <tbody>
                        {meds.map((m) => {
                            // Check doctor keys
                            const dGiven = d[m.key] === "Yes";
                            const dDose = d[`${m.key}_dose`] || d[`${m.key}Dose`] || "";
                            const dType = d[`${m.key}_type`] || d[`${m.key}Type`] || "";

                            // Check RN keys
                            const rnKey = `rn_${m.key}`; // assuming snake_case from Laravel
                            const rnCamelKey = `rn${m.key.charAt(0).toUpperCase() + m.key.slice(1)}`;

                            const rGiven = (d[rnKey] || d[rnCamelKey]) === "Yes";
                            const rDose = d[`${rnKey}_dose`] || d[`${rnCamelKey}Dose`] || "";
                            const rType = d[`${rnKey}_type`] || d[`${rnCamelKey}Type`] || "";

                            return (
                                <tr key={m.key}>
                                    <td style={{ textAlign: 'left', fontWeight: 'bold' }}>{m.label}</td>
                                    <td>{dGiven ? "Yes" : "No"}</td>
                                    <td>{dGiven ? `${dDose} (${dType})` : "-"}</td>
                                    <td>{rGiven ? "Yes" : "No"}</td>
                                    <td>{rGiven ? `${rDose} (${rType})` : "-"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Remarks Section */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '10px' }}>
                    <div>
                        <strong>Doctor Remarks:</strong>
                        <div className="text-box">{d.doctor_remarks || d.doctorRemarks}</div>
                    </div>
                    <div>
                        <strong>RN Remarks:</strong>
                        <div className="text-box">{d.rn_remarks || d.rnRemarks}</div>
                    </div>
                </div>

                <span className="section-title">On Duty Doctor / Consultant</span>
                <table className="sign-table">
                    <tbody>
                        <tr>
                            <td className="label1">Name</td>
                            <td className="value1">{d.doctor_name || d.doctorName}</td>
                            <td className="label1">Designation</td>
                            <td className="value2">{d.doctor_designation || d.doctorDesignation}</td>
                            <td className="label1">Adiministration Place</td>
                            <td className="value2">{d.doctor_place || d.doctorPlace}</td>
                        </tr>
                        <tr>
                            <td className="label1">Date</td>
                            <td className="value1"></td>
                            <td className="label1">Time</td>
                            <td className="value2"></td>
                            <td className="label1">Signature</td>
                            <td className="value2"></td>
                        </tr>
                    </tbody>
                </table>
                <span className="section-title">Assigned RN / Dispenser</span>
                <table className="sign-table">
                    <tbody>
                        <tr>
                            <td className="label1">Name</td>
                            <td className="value1">{d.rn_name || d.rnName}</td>
                            <td className="label1">Designation</td>
                            <td className="value2">{d.rn_designation || d.rnDesignation}</td>
                            <td className="label1">Adiministration Place</td>
                            <td className="value2">{d.rn_place || d.rnPlace}</td>
                        </tr>
                        <tr>
                            <td className="label1">Date</td>
                            <td className="value1"></td>
                            <td className="label1">Time</td>
                            <td className="value2"></td>
                            <td className="label1">Signature</td>
                            <td className="value2"></td>
                        </tr>
                    </tbody>
                </table>


                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}